import { useCallback, useEffect, useRef } from 'react';
import type { SyncState } from '../types.ts';
import type { FramePlayer } from './useFramePlayer.ts';
import type { SyncClient } from './useSyncClient.ts';

/** How often the controller re-states where the room is, in ms. */
const HEARTBEAT_MS = 500;

/**
 * The outgoing half of the sync: turns local state into room state.
 *
 * `snapshot` is everything about the local view that the room cares about; it is re-read on
 * every render, so callers can pass a plain object literal.
 *
 * Returns `pushState`, which is stable for the lifetime of the component. That matters:
 * `player` and `sync` are new objects on every render, so if pushState closed over them
 * directly, every effect depending on it would tear down and rebuild on each render — and the
 * heartbeat below would be cancelled before it ever fired, leaving followers with no idea
 * where the room is. Hence the refs.
 */
export function useRoomState(
  player: FramePlayer,
  sync: SyncClient,
  snapshot: Partial<SyncState>,
): (patch?: Partial<SyncState>) => void {
  const playerRef = useRef(player);
  playerRef.current = player;
  const syncRef = useRef(sync);
  syncRef.current = sync;
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;

  const pushState = useCallback((patch: Partial<SyncState> = {}) => {
    // Anyone who acts drives the room: the server promotes the sender to controller
    // (auto-handoff), so nobody has to pass the remote around by hand.
    const client = syncRef.current;
    const p = playerRef.current;
    client.sendState({
      ...snapshotRef.current,
      playing: p.playing,
      mediaTime: p.currentTime(),
      ...patch,
    });
  }, []);

  // Heartbeat: keeps late joiners and any follower that fell behind pinned to the room.
  const isController = sync.isController;
  useEffect(() => {
    if (!isController) return;
    const timer = window.setInterval(() => pushState(), HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [isController, pushState]);

  // Tell the room when this client has enough video buffered to start together.
  const bufferReady = player.buffered > 0.05 || player.ready;
  const setReady = sync.setReady;
  useEffect(() => {
    setReady(bufferReady);
  }, [bufferReady, setReady]);

  return pushState;
}
